// TablaAsignaciones.js
import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Tag, Button, Space, message, Pagination } from "antd";
import ConfirmarDesasignacionDialog from "./ConfirmarDesasignacionDialog";
import { useUser } from "../../../UserContext";

const TablaAsignaciones = ({
  asignaciones,
  zonaFiltro,
  searchTerm,
  onDeleteAsignacion,
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedAsignacion, setSelectedAsignacion] = useState(null);
  const { user: currentUser } = useUser();

  // Estados para paginación
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const hasPermission = (moduleKey, permission) => {
    const module = currentUser?.modulos?.find((m) => m.key === moduleKey);
    if (!module || module.estado !== "disponible") return false;
    return currentUser?.modulosPermisos?.some(
      (mp) => mp.modulo === moduleKey && mp.permiso === permission
    );
  };

  const filteredAsignaciones = useMemo(() => {
    return asignaciones.filter((asignacion) => {
      const vehiculo = asignacion.Vehiculo;
      const conductor = asignacion.Conductor;

      const matchesZona =
        zonaFiltro == null || (vehiculo && vehiculo.zona_id === zonaFiltro);

      // Los campos pueden venir vacíos (p. ej. vehículo sin código o conductor sin documento)
      const termino = (searchTerm || "").trim().toLowerCase();
      const contiene = (valor) =>
        valor != null && String(valor).toLowerCase().includes(termino);
      const matchesSearchTerm =
        !termino ||
        (vehiculo && (contiene(vehiculo.placa) || contiene(vehiculo.codigo))) ||
        (conductor &&
          (contiene(conductor.nombre) || contiene(conductor.numero_documento)));

      return matchesZona && matchesSearchTerm;
    });
  }, [asignaciones, zonaFiltro, searchTerm]);

   // Paginación local sobre datos filtrados
  const asignacionesPaginadas = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredAsignaciones.slice(start, start + rowsPerPage);
  }, [filteredAsignaciones, page, rowsPerPage]);

  // Resetear página al cambiar filtro
  useEffect(() => {
    setPage(1);
  }, [searchTerm, zonaFiltro]);

  const handleDelete = async (id) => {
    const asignacion = asignaciones.find((a) => a.id === id);
    setSelectedAsignacion(asignacion);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await onDeleteAsignacion(selectedAsignacion.id);
      message.success("Asignación eliminada correctamente");
      setConfirmDialogOpen(false);
      setSelectedAsignacion(null);
    } catch (error) {
      message.error("Error al eliminar la asignación");
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setSelectedAsignacion(null);
  };

  const columns = [
    {
      title: "Vehículo",
      dataIndex: "Vehiculo",
      key: "vehiculo",
      sorter: (a, b) => (a.Vehiculo?.placa || "").localeCompare(b.Vehiculo?.placa || ""),
      render: (vehiculo) => (
        <>
          <strong>{vehiculo?.placa || "N/A"}</strong>
          <br />
          {vehiculo?.codigo || "N/A"}
          <br />
          {vehiculo?.zona?.nombre || "N/A"}
        </>
      ),
    },
    {
      title: "Conductor",
      dataIndex: "Conductor",
      key: "conductor",
      sorter: (a, b) => (a.Conductor?.nombre || "").localeCompare(b.Conductor?.nombre || ""),
      render: (conductor) => (
        <>
          <strong>{conductor?.nombre || "N/A"}</strong>
          <br />
          {conductor?.numero_documento || "N/A"}
          <br />
          {conductor?.zona?.nombre || "N/A"}
        </>
      ),
    },
    {
      title: "Es Principal",
      dataIndex: "es_principal",
      key: "es_principal",
      filters: [
        { text: "Sí", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.es_principal === value,
      render: (text) =>
        text ? <Tag color="green">Sí</Tag> : <Tag color="volcano">No</Tag>,
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (text, record) => (
        <Space>
          {/* Proteger botón de desasignar */}
          {hasPermission("Transporte", "ELIMINAR_T") && (
            
            <Button
              type="primary"
              danger
              onClick={() => handleDelete(record.id)}
            >
              Desasignar Conductor
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={asignacionesPaginadas.map((asignacion) => ({
          key: `${asignacion.vehiculo_id}-${asignacion.conductor_id}`,
          ...asignacion,
        }))}
        pagination={false}
      />
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <Pagination
          current={page}
          pageSize={rowsPerPage}
          total={filteredAsignaciones.length}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          onChange={(newPage, newPageSize) => {
            setPage(newPage);
            setRowsPerPage(newPageSize);
          }}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} de ${total} asignaciones`
          }
        />
      </div>
      
      {/* Diálogo de confirmación protegido */}
      {confirmDialogOpen && hasPermission("Transporte", "ELIMINAR_T") && (
        <ConfirmarDesasignacionDialog
          open={confirmDialogOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          vehiculo={selectedAsignacion?.Vehiculo}
          conductor={selectedAsignacion?.Conductor}
        />
      )}
    </>
  );
};

TablaAsignaciones.propTypes = {
  asignaciones: PropTypes.array.isRequired,
  zonaFiltro: PropTypes.number,
  searchTerm: PropTypes.string.isRequired,
  onDeleteAsignacion: PropTypes.func.isRequired,
};

export default TablaAsignaciones;
